/* NewLicenseModal — v2 license creation flow.
 *
 * Three locked-in pieces of PR-4 behavior:
 *
 *   1. The preset catalog comes from GET /api/license/presets — never
 *      hardcoded in this component. If the server flags
 *      hasMissingPresets:true we surface a banner pointing ops at the
 *      seeder, so a partial seed (1/8 templates) is never silent.
 *
 *   2. Customize toggle. Off (default) = the preset's products / quotas
 *      / billing / duration appear as read-only chips so the admin sees
 *      what they're issuing without being able to slip a typo into a
 *      quota. On = every field becomes editable AND the preset value
 *      flips to "custom" so the resulting license accurately records
 *      that the admin overrode the catalog.
 *
 *   3. Scope picker drives the rest of the form:
 *        User    → email field with autocomplete against
 *                  /api/manage/users (typed substring match).
 *        Domain  → free-text input gated by a domain regex.
 *        Unbound → picker disabled; maxRedemptions input becomes
 *                  required + prominent with the documented helper text.
 *
 * The submit flow walks through the parent's onCreate (which calls
 * /api/license/create with the v2 body) and surfaces the generated
 * code in a success view with Copy. */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { P, FC } from "../../shared/constants";
import Modal from "../components/Modal";
import Chip from "../components/Chip";
import { Copy, Check, AlertTriangle } from "../icons";
var E = React.createElement;

/* Products available on a license. Keys match
   api/shared/entitlements.js#PRODUCT_KEYS and the v3 catalog in
   api/shared/license-presets.js. PR-5 split Watchtower into two SKUs
   so the matrix shows them as distinct rows. */
var PRODUCT_DEFS = [
  { key:"watchtower",          label:"Watchtower (Scan)",       hint:"Web accessibility scanning + delta recon" },
  { key:"watchtowerRemediate", label:"Watchtower (Remediate)",  hint:"Unlocks remediation actions on flagged issues" },
  { key:"deltaRecon",          label:"Delta Recon",             hint:"Side-by-side scan comparison + remediation reports" },
  { key:"docmaya",             label:"DocMaya",                 hint:"Runtime PDF transmorphing" },
  { key:"pa11yLab",            label:"PA11Y Lab",               hint:"Single-page audit + AI suggestions" }
];

/* Domain validator per the brief: at least one dot and a TLD of ≥2
   letters. Anchored, case-insensitive, no protocol or path. */
var DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

function todayPlus(days) {
  var d = new Date();
  d.setDate(d.getDate() + (days || 0));
  return d.toISOString().slice(0, 10);
}

/* ─── small helpers for the autocomplete listbox ─── */

function debounce(fn, ms) {
  var t;
  return function () {
    var args = arguments, ctx = this;
    clearTimeout(t);
    t = setTimeout(function () { fn.apply(ctx, args); }, ms);
  };
}

export default function NewLicenseModal(props) {
  /* ─── catalog (hydrated from /api/license/presets on open) ─── */
  var pState = useState({ loading: true, error: null, list: [], hasMissingPresets: false });
  var presetsState = pState[0], setPresetsState = pState[1];

  /* ─── user list for the email autocomplete ─── */
  var uState = useState([]);
  var users = uState[0], setUsers = uState[1];

  /* ─── form state ─── */
  function blankForm() {
    return {
      presetId:     "",
      scopeKind:    "user",
      userEmail:    "",
      domainKey:    "",
      maxRedemptions: "1",
      perpetual:    false,
      startsAt:     todayPlus(0),
      endsAt:       todayPlus(30),
      customize:    false,
      products:     null,   // copied from preset when picked
      billing:      null,
      notes:        ""
    };
  }
  var fState = useState(blankForm);
  var f = fState[0], setF = fState[1];
  function update(patch) { setF(Object.assign({}, f, patch)); }

  /* After-create success view: { license } */
  var sState = useState(null);
  var success = sState[0], setSuccess = sState[1];
  /* PR-5.9: inline error banner for create failures. Was using
     showToast(error,"error") which the admin could miss while focused
     on the modal — now the error string from the dispatcher renders
     in a red banner above the action buttons inside the dialog. */
  var eState = useState(null);
  var submitError = eState[0], setSubmitError = eState[1];
  /* In-flight flag so the submit button can show "Creating…" and the
     dialog can't double-fire on a slow round-trip. */
  var bState = useState(false);
  var submitting = bState[0], setSubmitting = bState[1];

  /* ─── load presets on open ─── */
  useEffect(function () {
    if (!props.open) return;
    setF(blankForm());
    setSuccess(null);
    setPresetsState(function (prev) { return Object.assign({}, prev, { loading: true, error: null }); });
    fetch("/api/license/presets", { credentials: "include" })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        setPresetsState({
          loading: false, error: null,
          list: data.presets || [],
          hasMissingPresets: !!data.hasMissingPresets,
          source:      data.source,
          fromCosmos:  data.fromCosmos  || 0,
          fromBundled: data.fromBundled || 0,
          expected:    data.expected    || 8
        });
      })
      .catch(function (err) {
        setPresetsState({
          loading: false, error: String((err && err.message) || err),
          list: [], hasMissingPresets: false
        });
      });
  }, [props.open]);

  /* ─── load users on open for the autocomplete (admin tab already
     fetches /api/manage/users in some flows but the modal can outlive
     the tab activation so we fetch independently). Cheap enough at
     <=200 users to keep client-side. ─── */
  useEffect(function () {
    if (!props.open) return;
    fetch("/api/manage/users?limit=200", { credentials: "include" })
      .then(function (r) { return r.ok ? r.json() : { users: [] }; })
      .then(function (data) { setUsers(data.users || []); })
      .catch(function () { setUsers([]); });
  }, [props.open]);

  /* ─── runSeeder — admin can fill the missing 7 of 8 preset templates
     without leaving the modal. POSTs to /api/license/seed-presets (an
     admin-only endpoint that calls scripts/seed-presets.js#runSeed
     against the live licenses container). On success we re-fetch
     /presets so the dropdown updates and the banner disappears. ─── */
  function runSeeder() {
    setPresetsState(function (prev) { return Object.assign({}, prev, { seeding: true }); });
    fetch("/api/license/seed-presets", { method: "POST", credentials: "include" })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (b) { throw new Error(b.error || "HTTP " + r.status); });
        }
        return r.json();
      })
      .then(function (data) {
        props.showToast && props.showToast("✅ Seeded " + (data.seeded || 0) + " preset templates");
        /* Re-fetch /presets so the dropdown reflects the new state and
           hasMissingPresets flips to false. */
        return fetch("/api/license/presets", { credentials: "include" })
          .then(function (r) { return r.ok ? r.json() : null; });
      })
      .then(function (data) {
        if (data && data.presets) {
          setPresetsState({
            loading: false, error: null, seeding: false,
            list: data.presets,
            hasMissingPresets: !!data.hasMissingPresets,
            source: data.source
          });
        } else {
          setPresetsState(function (prev) { return Object.assign({}, prev, { seeding: false }); });
        }
      })
      .catch(function (err) {
        setPresetsState(function (prev) { return Object.assign({}, prev, { seeding: false }); });
        props.showToast && props.showToast("Seed failed: " + (err && err.message || err), "error");
      });
  }

  /* ─── auto-fill on preset pick ───
     PR-5.6: preset.scope ∈ {"user","domain"} is a DEFAULT, not a
     lock. The picker shows all three radios (User / Domain /
     Unbound) for every preset; selecting a preset just pre-checks
     the radio that matches preset.scope. The admin can override —
     e.g. grant an Enterprise license to a single beta-tester by
     User scope without having to bind it to a whole domain. The
     server-side license-create path accepts any combination. */
  function selectPreset(presetId) {
    var p = presetsState.list.find(function (x) { return x.id === presetId; });
    if (!p) { update({ presetId: presetId }); return; }
    var durationDays = (p.validity && p.validity.durationDays) || null;
    var presetScope = p.scope === 'user' || p.scope === 'domain' ? p.scope : 'user';
    setF(Object.assign({}, f, {
      presetId: presetId,
      scopeKind: presetScope,
      products: JSON.parse(JSON.stringify(p.products || {})),
      billing:  JSON.parse(JSON.stringify(p.billing  || {})),
      perpetual: !durationDays,
      endsAt:   durationDays ? todayPlus(durationDays) : f.endsAt,
      customize: false       // picking a fresh preset resets the toggle
    }));
  }

  /* ─── customize toggle ─── */
  function toggleCustomize() {
    /* When the admin flips Customize on, the preset value moves to
       "custom" so the server-side record reflects that the admin
       deviated from the catalog. Flipping back off restores the
       original preset's defaults. */
    if (!f.customize) {
      update({ customize: true, presetId: "custom" });
    } else {
      /* Off → try to snap back to the last named preset that matches
         the current products map. If none, leave as custom. */
      update({ customize: false });
    }
  }

  /* ─── editing the entitlements matrix while in Customize mode ─── */
  function toggleProductEnabled(key) {
    if (!f.customize) return;
    var products = Object.assign({}, f.products || {});
    var current = Object.assign({}, products[key] || {});
    current.enabled = !current.enabled;
    products[key] = current;
    update({ products: products });
  }
  function updateProductField(key, field, value) {
    if (!f.customize) return;
    var products = Object.assign({}, f.products || {});
    products[key] = Object.assign({}, products[key] || {}, { [field]: value === "" ? "" : Number(value) });
    update({ products: products });
  }

  /* ─── validation ─── */
  var preset = useMemo(function () {
    return presetsState.list.find(function (x) { return x.id === f.presetId; }) || null;
  }, [presetsState.list, f.presetId]);

  var domainValid = f.scopeKind !== "domain" ? true : DOMAIN_RE.test(f.domainKey || "");
  var emailValid  = f.scopeKind !== "user"   ? true : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.userEmail || "");

  function maxRedemptionsParsed() {
    var s = (f.maxRedemptions || "").trim();
    if (s === "") return -1;        // unlimited (per brief helper text)
    var n = parseInt(s, 10);
    if (!isFinite(n)) return NaN;
    return n;
  }
  var maxR = maxRedemptionsParsed();
  var maxRValid = Number.isFinite(maxR) && (maxR === -1 || maxR >= 1);

  var canSubmit =
    !!f.presetId && f.presetId !== "" &&
    (f.scopeKind === "unbound" || (f.scopeKind === "user" && emailValid) || (f.scopeKind === "domain" && domainValid)) &&
    maxRValid;

  /* ─── submit ─── */
  function submit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) {
      props.showToast && props.showToast("Fix the fields highlighted in red before submitting.", "error");
      return;
    }
    /* Build the v2 payload. When the admin hasn't toggled Customize on,
       we send the preset id alone and let the server derive products /
       billing from the seeded template — keeps source-of-truth
       single. With Customize on, the modal sends the full override. */
    var payload = {
      preset: f.customize ? "custom" : f.presetId,
      scope: f.scopeKind === "unbound"
        ? { kind: "unbound", key: "UNBOUND" }
        : f.scopeKind === "user"
          ? { kind: "user",   key: f.userEmail.trim().toLowerCase() }
          : { kind: "domain", key: f.domainKey.trim().toLowerCase() },
      validity: {
        startsAt: new Date(f.startsAt).toISOString(),
        endsAt:   f.perpetual ? null : new Date(f.endsAt).toISOString()
      },
      maxRedemptions: maxR,
      notes: f.notes || ""
    };
    if (f.customize) {
      payload.products = f.products || {};
      payload.billing  = f.billing  || {};
    }

    setSubmitError(null);
    setSubmitting(true);
    Promise.resolve(props.onCreate && props.onCreate(payload)).then(function (result) {
      setSubmitting(false);
      if (result && result.code) {
        setSuccess({ license: result });
      } else if (result === true) {
        props.onClose && props.onClose();
      } else if (result && result.error) {
        /* The parent's createLicenseFromPayload returns the raw
           server response when an error key is present (PR-5.9 wiring).
           Render the message inline so the admin sees it without
           hunting for the toast. */
        setSubmitError(String(result.error));
      } else if (result === false) {
        setSubmitError("Create failed. Check the network panel for details.");
      }
    }).catch(function (e) {
      setSubmitting(false);
      setSubmitError(String((e && e.message) || e));
    });
  }

  /* ─── styles ─── */
  function inputStyle(extra) {
    return Object.assign({}, {
      width: "100%", padding: "10px 12px", borderRadius: 8,
      border: "1px solid " + P.bdr,
      fontSize: 13, minHeight: 40, background: P.card, color: P.slate
    }, extra || {});
  }
  function labelStyle() {
    return { display: "block", fontSize: 11, fontWeight: 700, color: P.slateM, marginBottom: 6 };
  }
  function hintStyle() {
    return { fontSize: 11, color: P.slateM, marginTop: 6, lineHeight: 1.5 };
  }
  function errorStyle() {
    return { fontSize: 11, color: P.coralDk || P.coral, marginTop: 4, fontWeight: 600 };
  }

  /* ─── email autocomplete listbox ─── */
  var listboxId = "nlm-user-listbox";
  var emailMatches = useMemo(function () {
    var q = (f.userEmail || "").trim().toLowerCase();
    if (q.length < 2) return [];
    return users.filter(function (u) {
      var em = (u.email || u.name || "").toLowerCase();
      var nm = (u.name || "").toLowerCase();
      return em.indexOf(q) >= 0 || nm.indexOf(q) >= 0;
    }).slice(0, 8);
  }, [f.userEmail, users]);
  var openListState = useState(false);
  var openList = openListState[0], setOpenList = openListState[1];

  /* ─── success view ─── */
  function copy(text, label) {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(function () {
      props.showToast && props.showToast((label || "Copied") + " to clipboard");
    });
  }
  function successBody() {
    var lic = success.license;
    var scopeIcon = lic.scope && lic.scope.kind === "user" ? "👤" : lic.scope && lic.scope.kind === "domain" ? "🌐" : "🎟";
    return E("div", { role: "status", style: { display: "flex", flexDirection: "column", gap: 14 } },
      E("div", {
        style: {
          display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10,
          background: P.emeraldLt, border: "1px solid " + (P.emerald) + "30",
          alignItems: "flex-start", color: P.emeraldDk || P.emerald
        }
      },
        E(Check, { size: 18 }),
        E("div", { style: { fontSize: 12, fontWeight: 700 } },
          "License created. The code below was generated by the server — copy it now; ",
          "the admin Licenses tab also shows it in the row detail panel.")),
      E("div", null,
        E("div", { style: labelStyle() }, "License code"),
        E("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
          E("code", {
            style: {
              flex: 1, padding: "10px 12px", borderRadius: 8,
              border: "1px solid " + P.bdr, background: P.bgS,
              fontFamily: FC, fontSize: 12, wordBreak: "break-all"
            }
          }, lic.code),
          E("button", {
            type: "button",
            "aria-label": "Copy license code",
            onClick: function () { copy(lic.code, "License code"); },
            style: {
              padding: "8px 12px", borderRadius: 8, border: "1px solid " + P.bdr,
              background: P.card, cursor: "pointer", display: "inline-flex",
              alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, minHeight: 40
            }
          }, E(Copy, { size: 14 }), "Copy"))),
      E("dl", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: 0 } },
        E("div", null,
          E("dt", { style: { fontSize: 10, fontWeight: 700, color: P.slateM, textTransform: "uppercase" } }, "Scope"),
          E("dd", { style: { fontSize: 12, color: P.slate, margin: 0 } },
            scopeIcon, " ", lic.scope && lic.scope.kind, lic.scope && lic.scope.kind !== "unbound"
              ? " · " + lic.scope.key : "")),
        E("div", null,
          E("dt", { style: { fontSize: 10, fontWeight: 700, color: P.slateM, textTransform: "uppercase" } }, "Preset"),
          E("dd", { style: { fontSize: 12, color: P.slate, margin: 0 } }, lic.preset)),
        E("div", null,
          E("dt", { style: { fontSize: 10, fontWeight: 700, color: P.slateM, textTransform: "uppercase" } }, "Starts"),
          E("dd", { style: { fontSize: 12, color: P.slate, margin: 0 } },
            (lic.validity && lic.validity.startsAt || "").split("T")[0] || "—")),
        E("div", null,
          E("dt", { style: { fontSize: 10, fontWeight: 700, color: P.slateM, textTransform: "uppercase" } }, "Expires"),
          E("dd", { style: { fontSize: 12, color: P.slate, margin: 0 } },
            lic.validity && lic.validity.endsAt
              ? lic.validity.endsAt.split("T")[0]
              : "Perpetual"))));
  }

  /* ─── form body ─── */
  function formBody() {
    return E("form", { onSubmit: submit, noValidate: true },
      /* Banner: only renders when at least one preset is bundled-only
         (i.e. Cosmos hasn't been seeded with the full v3 catalog yet).
         When all 8 are in Cosmos we hide the banner entirely. PR-5.2
         copy explicitly reports the split — "N from Cosmos, M from
         bundled" — so the operator can see at a glance whether a
         re-seed is needed. */
      presetsState.hasMissingPresets ? E("div", {
        role: "alert",
        style: {
          display: "flex", gap: 10, padding: "10px 12px", borderRadius: 8,
          background: P.amberLt, border: "1px solid " + (P.amber || "#FBBF24") + "40",
          marginBottom: 14, color: P.amberDk || P.amber,
          alignItems: "flex-start", flexWrap: "wrap"
        }
      },
        E(AlertTriangle, { size: 16 }),
        E("div", { style: { fontSize: 11, lineHeight: 1.5, flex: "1 1 280px" } },
          E("strong", null, (presetsState.expected || 8) + " presets ready"),
          " (", presetsState.fromCosmos || 0, " from Cosmos, ",
          presetsState.fromBundled || 0, " from bundled). ",
          "Click ", E("strong", null, "Run seeder"),
          " to upsert the bundled ones into Cosmos so customisations stick across deploys, ",
          "or run ", E("code", { style: { fontFamily: FC } }, "node scripts/seed-presets.js"),
          " from a shell with ", E("code", { style: { fontFamily: FC } }, "COSMOS_CONN_STRING"), " set."),
        E("button", {
          type: "button",
          disabled: presetsState.seeding,
          onClick: function () { runSeeder(); },
          style: {
            padding: "8px 12px", borderRadius: 8, border: "1px solid " + (P.amber || "#FBBF24"),
            background: P.amber || "#FBBF24", color: "#fff",
            fontSize: 11, fontWeight: 700,
            cursor: presetsState.seeding ? "wait" : "pointer", minHeight: 36, alignSelf: "center"
          }
        }, presetsState.seeding ? "Seeding…" : "Run seeder")
      ) : null,

      /* PR-5.9: inline submit-error banner. Renders the dispatcher's
         error string in red above the form when create returns non-2xx
         (the most common failure is the duplicate-domain rejection at
         license/index.js#handleCreate). Was previously surfaced only
         via showToast which the admin could miss while focused on
         the dialog. */
      submitError ? E("div", {
        role: "alert", "aria-live": "polite",
        style: {
          padding: "10px 12px", borderRadius: 8,
          background: P.coralLt, border: "1px solid " + P.coral + "40",
          marginBottom: 14, color: P.coralDk || P.coral, fontSize: 12, fontWeight: 600,
          display: "flex", gap: 8, alignItems: "flex-start"
        }
      },
        E(AlertTriangle, { size: 16 }),
        E("div", { style: { flex: 1, lineHeight: 1.5 } }, submitError)
      ) : null,

      presetsState.error ? E("div", {
        role: "alert",
        style: {
          padding: "10px 12px", borderRadius: 8,
          background: P.coralLt, border: "1px solid " + P.coral + "40",
          marginBottom: 14, color: P.coralDk || P.coral, fontSize: 11
        }
      }, "Couldn't load presets: ", presetsState.error) : null,

      E("div", { style: { display: "grid", gap: 14, gridTemplateColumns: "1fr 1fr" } },

        /* ── Preset dropdown ── */
        E("div", { style: { gridColumn: "1 / -1" } },
          E("label", { htmlFor: "nlm-preset", style: labelStyle() }, "Preset"),
          E("select", {
            id: "nlm-preset",
            value: f.presetId,
            disabled: presetsState.loading,
            onChange: function (e) { selectPreset(e.target.value); },
            required: true,
            style: inputStyle()
          },
            E("option", { value: "" }, presetsState.loading ? "Loading presets…" : "— select a preset —"),
            presetsState.list.map(function (p) {
              return E("option", { key: p.id, value: p.id },
                p.name + (p.shortCode ? " (" + p.shortCode + ")" : "") +
                (p.billing && p.billing.priceUsd ? " · $" + p.billing.priceUsd + "/" + (p.billing.model === "perpetual" ? "perpetual" : "mo") : ""));
            }),
            /* PR-5.10 bug fix: render the "Custom" option in the
               dropdown whenever the admin has flipped Customize on.
               toggleCustomize sets presetId="custom" so the form
               state can carry the override flag to the server, but
               without a matching <option> the browser would silently
               drop the value back to "" and any assertion on
               select.value === "custom" would fail. Conditional so
               the dropdown stays clean for the normal flow. */
            f.customize ? E("option", { key: "custom", value: "custom" },
              "Custom (admin override)") : null),
          preset && preset.description
            ? E("p", { style: hintStyle() }, preset.description) : null),

        /* ── Customize toggle ──
           PR-5.10 bug fix: this used to be `preset ? E(...)` which
           UNMOUNTED the toggle the instant toggleCustomize fired —
           setting presetId="custom" made the `preset` useMemo return
           null, dropping the entire block (checkbox + label) out of
           the DOM. Playwright's `.check()` then waited 30s for the
           checked state to flip on a checkbox that had vanished.
           The render now stays mounted whenever the admin has either
           a named preset OR has toggled customize on, so the
           checkbox's state transition is observable. */
        (preset || f.customize) ? E("div", { style: { gridColumn: "1 / -1" } },
          E("label", {
            style: {
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 12px", borderRadius: 8,
              border: "1px solid " + (f.customize ? P.pri : P.bdr),
              background: f.customize ? P.priLt : P.card, cursor: "pointer"
            }
          },
            E("input", {
              type: "checkbox", checked: !!f.customize,
              onChange: toggleCustomize,
              style: { width: 16, height: 16, accentColor: P.pri }
            }),
            E("span", { style: { fontSize: 12, fontWeight: 700, color: P.slate } }, "Customize"),
            E("span", { style: { fontSize: 11, color: P.slateM } },
              f.customize
                ? "— editing as a custom license (preset will record \"custom\")"
                : "— quotas + billing locked to the preset"))) : null,

        /* ── Scope ── */
        E("fieldset", {
          style: {
            gridColumn: "1 / -1", border: "1px solid " + P.bdr, borderRadius: 10,
            padding: "12px 14px", margin: 0
          }
        },
          E("legend", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, padding: "0 6px" } }, "Scope"),
          /* PR-5.6: all three scope options are ALWAYS selectable.
             preset.scope just pre-checks the matching radio when a
             preset is picked (see selectPreset). The admin can
             override — e.g. grant Enterprise to a single beta user
             via User scope without binding the whole domain. */
          E("div", { role: "radiogroup", "aria-label": "License scope", style: { display: "flex", gap: 8, flexWrap: "wrap" } },
            [
              { id: "user",    label: "User", hint: "Bind to a single email" },
              { id: "domain",  label: "Domain", hint: "Anyone at @org" },
              { id: "unbound", label: "Unbound (coupon)", hint: "Code-redeemable" }
            ].map(function (s) {
              var on = f.scopeKind === s.id;
              return E("label", {
                key: s.id,
                style: {
                  display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                  borderRadius: 8, border: "1px solid " + (on ? P.pri : P.bdr),
                  background: on ? P.priLt : P.card, cursor: "pointer", flex: "1 1 200px"
                }
              },
                E("input", {
                  type: "radio", name: "nlm-scope", value: s.id, checked: on,
                  onChange: function () { update({ scopeKind: s.id }); },
                  style: { accentColor: P.pri }
                }),
                E("div", { style: { display: "flex", flexDirection: "column" } },
                  E("span", { style: { fontSize: 12, fontWeight: 700, color: P.slate } }, s.label),
                  E("span", { style: { fontSize: 11, color: P.slateM } }, s.hint)));
            })),

          /* Scope picker per kind */
          f.scopeKind === "user" ? E("div", { style: { marginTop: 12, position: "relative" } },
            E("label", { htmlFor: "nlm-user", style: labelStyle() }, "User email"),
            E("input", {
              id: "nlm-user",
              type: "email", role: "combobox",
              "aria-autocomplete": "list",
              "aria-expanded": openList && emailMatches.length > 0 ? "true" : "false",
              "aria-controls": listboxId,
              autoComplete: "off",
              value: f.userEmail,
              onChange: function (e) { update({ userEmail: e.target.value }); setOpenList(true); },
              onFocus: function () { setOpenList(true); },
              onBlur: function () { setTimeout(function () { setOpenList(false); }, 100); },
              placeholder: "richard.delia@pi-consulting.us",
              required: true,
              style: inputStyle({
                fontFamily: FC,
                borderColor: f.userEmail && !emailValid ? P.coral : P.bdr
              })
            }),
            openList && emailMatches.length > 0 ? E("ul", {
              id: listboxId, role: "listbox",
              style: {
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                listStyle: "none", margin: "4px 0 0", padding: 4,
                background: P.card, border: "1px solid " + P.bdr, borderRadius: 8,
                maxHeight: 240, overflowY: "auto",
                boxShadow: "0 12px 32px rgba(0,0,0,.12)"
              }
            }, emailMatches.map(function (u) {
              return E("li", {
                key: u.id, role: "option",
                onMouseDown: function (e) {
                  e.preventDefault();
                  update({ userEmail: u.email || u.name });
                  setOpenList(false);
                },
                style: {
                  padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                  fontSize: 12, color: P.slate
                },
                onMouseEnter: function (e) { e.currentTarget.style.background = P.bgS; },
                onMouseLeave: function (e) { e.currentTarget.style.background = "transparent"; }
              },
                E("div", { style: { fontWeight: 600 } }, u.name || u.email || u.id),
                E("div", { style: { fontSize: 11, color: P.slateM, fontFamily: FC } }, u.email || u.id));
            })) : null,
            f.userEmail && !emailValid
              ? E("div", { style: errorStyle() }, "Enter a valid email address.")
              : null) : null,

          f.scopeKind === "domain" ? E("div", { style: { marginTop: 12 } },
            E("label", { htmlFor: "nlm-domain", style: labelStyle() }, "Domain"),
            E("input", {
              id: "nlm-domain",
              type: "text",
              value: f.domainKey,
              onChange: function (e) { update({ domainKey: e.target.value }); },
              placeholder: "amgen.com",
              required: true,
              autoComplete: "off",
              style: inputStyle({
                fontFamily: FC,
                borderColor: f.domainKey && !domainValid ? P.coral : P.bdr
              })
            }),
            f.domainKey && !domainValid
              ? E("div", { style: errorStyle() }, "Enter a bare domain like ", E("code", { style: { fontFamily: FC } }, "amgen.com"), ".")
              : E("p", { style: hintStyle() }, "Anyone with an email at this domain can sign in and redeem.")) : null,

          f.scopeKind === "unbound" ? E("p", { style: Object.assign({}, hintStyle(), { marginTop: 12 }) },
            "Unbound coupons aren't pre-assigned to anyone. The admin shares the generated code; ",
            "any signed-in user who pastes it on ",
            E("code", { style: { fontFamily: FC } }, "/#/redeem"),
            " gets bound automatically.") : null
        ),

        /* ── Max redemptions ── */
        E("div", null,
          E("label", { htmlFor: "nlm-max", style: labelStyle() },
            f.scopeKind === "unbound"
              ? E("span", null, "Max redemptions ", E("span", { style: { color: P.coral } }, "*"))
              : "Max redemptions"),
          E("input", {
            id: "nlm-max",
            type: "text", inputMode: "numeric",
            value: f.maxRedemptions,
            onChange: function (e) { update({ maxRedemptions: e.target.value }); },
            placeholder: f.scopeKind === "unbound" ? "1 (or blank for unlimited)" : "1",
            required: f.scopeKind === "unbound",
            style: inputStyle({
              fontWeight: f.scopeKind === "unbound" ? 700 : 400,
              borderColor: !maxRValid ? P.coral : P.bdr
            })
          }),
          E("p", { style: hintStyle() },
            "1 = single redemption, N = team coupon, leave blank for unlimited"),
          !maxRValid
            ? E("div", { style: errorStyle() }, "Enter a positive integer, or leave blank for unlimited.")
            : null),

        /* ── Validity ── */
        E("div", null,
          E("label", { htmlFor: "nlm-starts", style: labelStyle() }, "Starts on"),
          E("input", {
            id: "nlm-starts", type: "date", value: f.startsAt,
            onChange: function (e) { update({ startsAt: e.target.value }); },
            style: inputStyle()
          })),
        E("div", null,
          E("label", { htmlFor: "nlm-ends", style: labelStyle() }, "Expires on"),
          E("input", {
            id: "nlm-ends", type: "date", value: f.endsAt,
            onChange: function (e) { update({ endsAt: e.target.value }); },
            disabled: f.perpetual,
            style: inputStyle({ opacity: f.perpetual ? .5 : 1 })
          }),
          E("label", {
            style: {
              display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8,
              fontSize: 12, color: P.slateM, cursor: "pointer"
            }
          },
            E("input", {
              type: "checkbox", checked: f.perpetual,
              onChange: function (e) { update({ perpetual: e.target.checked }); },
              style: { accentColor: P.pri }
            }),
            "Perpetual (no expiry)")),

        /* ── Products matrix ── */
        E("fieldset", {
          style: {
            gridColumn: "1 / -1", border: "1px solid " + P.bdr, borderRadius: 10,
            padding: "12px 14px", margin: 0
          }
        },
          E("legend", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, padding: "0 6px" } }, "Products"),
          !f.products
            ? E("p", { style: Object.assign({}, hintStyle(), { margin: 0 }) }, "Pick a preset to see its products.")
            : E("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 8 } },
                PRODUCT_DEFS.map(function (p) {
                  var ent = (f.products && f.products[p.key]) || { enabled: false };
                  var on = !!ent.enabled;
                  var quotaFields = Object.keys(ent).filter(function (k) { return k !== "enabled" && typeof ent[k] === "number"; });
                  return E("div", {
                    key: p.key,
                    style: {
                      padding: "10px 12px", borderRadius: 8,
                      border: "1px solid " + (on ? P.pri : P.bdr),
                      background: on ? P.priLt : P.card,
                      opacity: f.customize ? 1 : (on ? 1 : .55)
                    }
                  },
                    E("label", { style: { display: "flex", gap: 8, alignItems: "flex-start", cursor: f.customize ? "pointer" : "default" } },
                      E("input", {
                        type: "checkbox", checked: on, disabled: !f.customize,
                        onChange: function () { toggleProductEnabled(p.key); },
                        style: { marginTop: 2, width: 18, height: 18, accentColor: P.pri }
                      }),
                      E("div", { style: { flex: 1 } },
                        E("div", { style: { fontSize: 12, fontWeight: 700, color: P.slate } }, p.label),
                        E("div", { style: { fontSize: 11, color: P.slateM, marginTop: 2 } }, p.hint))),
                    /* Quota chips / inputs */
                    quotaFields.length > 0 ? E("div", {
                      style: { marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }
                    }, quotaFields.map(function (k) {
                      return f.customize
                        ? E("label", {
                            key: k,
                            style: { display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: P.slateM }
                          },
                            E("span", null, k, ":"),
                            E("input", {
                              type: "number", value: String(ent[k]),
                              onChange: function (e) { updateProductField(p.key, k, e.target.value); },
                              style: {
                                width: 80, padding: "4px 6px", borderRadius: 4,
                                border: "1px solid " + P.bdr, fontSize: 11,
                                fontFamily: FC, color: P.slate, background: P.card
                              }
                            }))
                        : E(Chip, { key: k, variant: "slate", size: "sm" }, k + ": " + ent[k]);
                    })) : null);
                }))),

        /* ── Billing (only editable in customize mode) ── */
        f.billing ? E("fieldset", {
          style: {
            gridColumn: "1 / -1", border: "1px solid " + P.bdr, borderRadius: 10,
            padding: "12px 14px", margin: 0
          }
        },
          E("legend", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, padding: "0 6px" } }, "Billing"),
          f.customize
            ? E("div", { style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 } },
                E("div", null,
                  E("label", { htmlFor: "nlm-bm", style: labelStyle() }, "Model"),
                  E("select", {
                    id: "nlm-bm", value: f.billing.model || "rolling",
                    onChange: function (e) { update({ billing: Object.assign({}, f.billing, { model: e.target.value }) }); },
                    style: inputStyle()
                  },
                    E("option", { value: "rolling" },   "Rolling"),
                    E("option", { value: "prepaid" },   "Prepaid"),
                    E("option", { value: "perpetual" }, "Perpetual"))),
                E("div", null,
                  E("label", { htmlFor: "nlm-bp", style: labelStyle() }, "Price (USD)"),
                  E("input", {
                    id: "nlm-bp", type: "number",
                    value: String(f.billing.priceUsd || 0),
                    onChange: function (e) { update({ billing: Object.assign({}, f.billing, { priceUsd: Number(e.target.value) }) }); },
                    style: inputStyle()
                  })),
                E("div", null,
                  E("label", { htmlFor: "nlm-bc", style: labelStyle() }, "Currency"),
                  E("input", {
                    id: "nlm-bc", type: "text",
                    value: f.billing.currency || "USD",
                    onChange: function (e) { update({ billing: Object.assign({}, f.billing, { currency: e.target.value.toUpperCase() }) }); },
                    maxLength: 3,
                    style: inputStyle()
                  })))
            : E("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" } },
                E(Chip, { variant: "slate", size: "sm" }, "Model: " + (f.billing.model || "—")),
                E(Chip, { variant: "slate", size: "sm" }, "Price: $" + (f.billing.priceUsd || 0) + " " + (f.billing.currency || "USD"))))
        : null,

        /* ── Notes ── */
        E("div", { style: { gridColumn: "1 / -1" } },
          E("label", { htmlFor: "nlm-notes", style: labelStyle() }, "Notes (internal)"),
          E("textarea", {
            id: "nlm-notes", value: f.notes, rows: 2,
            onChange: function (e) { update({ notes: e.target.value }); },
            placeholder: "e.g. Pi Consulting launch promo for CSUN attendees",
            style: inputStyle({ minHeight: 60, resize: "vertical", lineHeight: 1.4 })
          }))
      ),
      /* Hidden submit so Enter in any field works through <form> association. */
      E("button", {
        type: "submit", tabIndex: -1, "aria-hidden": "true",
        style: {
          position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
          overflow: "hidden", clip: "rect(0,0,0,0)", border: 0
        }
      }, "")
    );
  }

  return E(Modal, {
    open: props.open, onClose: props.onClose,
    title: success ? "License created" : "New license",
    size: "lg",
    footer: success ? [
      E("button", {
        key: "done", type: "button", onClick: props.onClose,
        style: {
          padding: "8px 14px", borderRadius: 8, border: "none",
          background: P.pri, color: "#fff", fontSize: 11, fontWeight: 700,
          cursor: "pointer", minHeight: 36
        }
      }, "Done")
    ] : [
      E("button", {
        key: "cancel", type: "button", onClick: props.onClose,
        style: {
          padding: "8px 14px", borderRadius: 8, border: "1px solid " + P.bdr,
          background: P.card, fontSize: 11, fontWeight: 600,
          cursor: "pointer", minHeight: 36
        }
      }, "Cancel"),
      E("button", {
        key: "submit", type: "button",
        disabled: !canSubmit || submitting,
        onClick: submit,
        style: {
          padding: "8px 14px", borderRadius: 8, border: "none",
          background: (canSubmit && !submitting) ? P.pri : P.slateM, color: "#fff",
          fontSize: 11, fontWeight: 700,
          cursor: (canSubmit && !submitting) ? "pointer" : "not-allowed", minHeight: 36
        }
      }, submitting ? "Creating…" : "Create & copy code")
    ]
  }, success ? successBody() : formBody());
}
